/**
 * GroundTruth field descriptor — the single source of truth for the GT review
 * UI, loaded from the bundled JSON document generated out of
 * gt_io_ui_field_spec.xlsx (see scripts/generateGtFieldDescriptor.js).
 *
 * Every projection the rest of the frontend consumes is derived HERE:
 *
 *   GT_GROUP_COLS       ← fields[].gridColumns
 *   GT_GROUP_KEYS       ← descriptor.groups
 *   GT_FIELD_EDITORS    ← fields[].control
 *   GT_NON_EDITABLE     ← fields[].editable + the sticky fmt/channel cells
 *   GT_ROW_TO_AG        ← fields[].rowKey → fields[].writeBack
 *   GT_ISSUE_FIELD_MAP  ← fields[].configKey → fields[].rowKey
 *   GT_LIST_FIELDS      ← fields with lookup/multi-value capabilities
 *
 * A served descriptor (GCS, versioned like the existing gt-config-revisions)
 * can replace the bundled one later without touching any consumer: register it
 * with registerGtFieldDescriptor() before the grid mounts. The loader contract
 * is deliberately just a plain object — how it arrives is the host's decision.
 */

import descriptorJson from './gtFieldDescriptor.json';

let activeDescriptor = descriptorJson;

/** Override the bundled descriptor at runtime (e.g. a GCS-served version). */
export function registerGtFieldDescriptor(descriptor) {
  if (descriptor && typeof descriptor === 'object' && descriptor.fields) {
    activeDescriptor = descriptor;
    return true;
  }
  return false;
}

/** The descriptor currently active. Callers needing live updates re-derive. */
export function getGtFieldDescriptor() {
  return activeDescriptor;
}

/** One field entry by row key, or null. */
export function getGtField(fieldKey) {
  return activeDescriptor?.fields?.[fieldKey] || null;
}

/** Normalise a descriptor's group order from its field entries. */
function descriptorGroups(desc) {
  if (Array.isArray(desc?.groups) && desc.groups.length) return desc.groups;
  const seen = [];
  for (const entry of Object.values(desc?.fields || {})) {
    for (const col of entry.gridColumns || []) {
      if (col.tab && !seen.includes(col.tab)) seen.push(col.tab);
    }
  }
  return seen;
}

// ── Column projections ─────────────────────────────────────────────────────

/** GT_GROUP_COLS projection: { tab: [{ k, l, w, fmt? }] }. */
export function gtGroupCols(desc = activeDescriptor) {
  const groups = {};
  for (const group of descriptorGroups(desc)) groups[group] = [];
  const placed = [];
  for (const entry of Object.values(desc?.fields || {})) {
    for (const col of entry.gridColumns || []) {
      if (!col || !col.tab) continue;
      placed.push({
        position: Number.isFinite(col.position) ? col.position : Number.MAX_SAFE_INTEGER,
        tab: col.tab,
        projected: {
          k: entry.rowKey,
          l: col.label || entry.label,
          w: col.width,
          ...(col.fmt ? { fmt: col.fmt } : {}),
        },
      });
    }
  }
  // The Fields sheet is ordered by config field; within a tab the descriptor
  // carries the legacy column position explicitly.
  placed.sort((a, b) => a.position - b.position || a.tab.localeCompare(b.tab));
  for (const { tab, projected } of placed) groups[tab].push(projected);
  return groups;
}

/** GT_GROUP_KEYS projection, in descriptor order. */
export function gtGroupKeys(desc = activeDescriptor) {
  return descriptorGroups(desc);
}

// ── Editing projections ─────────────────────────────────────────────────────

/** Is this entry a grid-rendered static single-select (a fixed option set)? */
function isGridStaticSelect(entry) {
  return String(entry?.control || '').toLowerCase() === 'single-select'
    && !entry?.lookup
    && (entry?.staticValues || []).length > 0
    && (entry?.gridColumns || []).length > 0;
}

/** GT_FIELD_EDITORS projection: row key → editor input type. */
export function gtFieldEditors(desc = activeDescriptor) {
  const out = {};
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    const control = String(entry.control || 'text').toLowerCase();
    if (control === 'currency' || control === 'number' || control === 'date') {
      out[key] = control;
    } else if (isGridStaticSelect(entry)) {
      // Static single-select grid cells (closed enums: bid_type, product, …).
      // Lookup-backed single-selects (location_group, tracking_visits_to) are
      // list cells, not scalar editors, so they stay out of this map.
      out[key] = 'select';
    }
  }
  return out;
}

/** GT_STATIC_OPTIONS projection: row key → fixed option list for 'select' editors. */
export function gtStaticOptions(desc = activeDescriptor) {
  const out = {};
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    if (isGridStaticSelect(entry)) out[key] = entry.staticValues;
  }
  return out;
}

/**
 * GT_NON_EDITABLE projection. Only fields the grid actually renders join the
 * denylist (a config-only field marked editable=false has no cell to gate).
 * The sticky `fmt`/`channel` cells are denylisted for the same reason as on
 * the other platforms (derived / launcher-owned), and each descriptor field
 * marked editable=false joins them. Each entry's `readOnlyReason` stays
 * available through gtReadOnlyReasons().
 */
export function gtNonEditable(desc = activeDescriptor) {
  const set = new Set(['fmt', 'channel']);
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    if (entry.editable === false && ((entry.gridColumns || []).length > 0 || key === 'name')) {
      set.add(key);
    }
  }
  return set;
}

/** Read-only reasons keyed by row key, for panels that want to show WHY. */
export function gtReadOnlyReasons(desc = activeDescriptor) {
  const out = {};
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    if (entry.editable === false) out[key] = entry.readOnlyReason || 'Read-only';
  }
  return out;
}

/** GT_ROW_TO_AG projection — non-identity rowKey → config write-back key. */
export function gtRowToAg(desc = activeDescriptor) {
  const out = {};
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    // Ad-group fields map whether or not they currently render a column:
    // UAT 0817 removed several columns whose CONFIG values stay editable
    // (agent path, issue-bar jump), and their writes must still land on the
    // right adgroup property. Campaign/config-scope rows stay out — their
    // writes go through the gt-campaign sentinel, not this map.
    const mappable = entry.scope === 'adGroup'
      || (entry.gridColumns || []).length > 0
      || key === 'name';
    if (mappable && entry.writeBack && entry.writeBack !== key) out[key] = entry.writeBack;
  }
  return out;
}

/**
 * The GT editable set, derived from the DESCRIPTOR rather than from grid
 * columns: every ad-group-scope field not marked read-only, plus the sticky
 * name cell. UAT 0817 removed several grid COLUMNS whose values remain
 * editable through the agent and the issue-bar jump (proximity_mode is
 * required for On Premise per R6 — a converter miss must stay fixable).
 * Read-only fields (editable === false) stay out regardless of columns.
 */
export function gtEditableFields(desc = activeDescriptor) {
  const set = new Set(['name']);
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    if (entry.scope !== 'adGroup') continue;
    if (entry.editable === false) continue;
    set.add(key);
  }
  return set;
}

/** GT_ISSUE_FIELD_MAP projection — validator config key → grid row key. */
export function gtIssueFieldMap(desc = activeDescriptor) {
  const out = {};
  for (const entry of Object.values(desc?.fields || {})) {
    if (entry.configKey && entry.rowKey) out[entry.configKey] = entry.rowKey;
  }
  return out;
}

// ── List-cell capabilities ──────────────────────────────────────────────────

/**
 * GT_LIST_FIELDS projection — the CellDetailPanel capability config for every
 * descriptor field that is multi-value OR lookup-backed. Capability flags come
 * straight off the Fields sheet's Add / Edit / Remove columns.
 */
export function gtListFields(desc = activeDescriptor) {
  const out = {};
  for (const [key, entry] of Object.entries(desc?.fields || {})) {
    if (entry.multiValue !== true && !entry.lookup) continue;
    const caps = entry.capabilities || {};
    const hasAnyOp = caps.add === true || caps.edit === true || caps.remove === true;
    if (!hasAnyOp && !entry.lookup) continue;
    const cfg = {
      ops: {
        add: caps.add === true,
        edit: caps.edit === true,
        remove: caps.remove === true,
      },
    };
    if (entry.lookup) {
      // types: the field's allowed taxonomy types, or null when the field
      // spans ALL types (the merged audiences cell). An empty array in the
      // descriptor means "all", so it must not survive the projection as []
      // or the picker would read it as an empty option list.
      cfg.lookup = {
        kind: entry.lookup.kind,
        types: Array.isArray(entry.lookup.types) && entry.lookup.types.length > 0
          ? entry.lookup.types : null,
      };
      // Lookup-backed edits REPLACE the pick (the id travels with the new
      // record) — never a free-text relabel.
      cfg.editViaLookup = true;
    }
    if (!caps.add) {
      cfg.reason = entry.readOnlyReason || 'This field cannot be edited here';
    }
    if (entry.multiValue === false) cfg.single = true;
    if (entry.addPlaceholder) cfg.addPlaceholder = entry.addPlaceholder;
    if (entry.note) cfg.note = entry.note;
    if (entry.appliesToProducts && entry.appliesToProducts.length) {
      cfg.appliesToProducts = entry.appliesToProducts;
      // R14's wording is the descriptor's own rule record — one message in
      // one place, like every other rule.
      const productRule = (desc?.rules || {})[(entry.ruleIds || []).find((id) => /^R14$/.test(id))];
      if (productRule?.message) cfg.productReason = productRule.message;
    }
    out[key] = cfg;
  }
  return out;
}

// ── Campaign summary card ───────────────────────────────────────────────────

/**
 * The campaign flight, as summary-card entries.
 *
 * These live in CODE rather than in gtFieldDescriptor.json because the JSON is
 * generated from gt_io_ui_field_spec.xlsx (see the module header) and that
 * sheet has no row for them — a hand-edit to the JSON would be reverted by the
 * next regeneration. They are nonetheless real campaign storage, not UI
 * decoration: `campaign.start_date` / `campaign.end_date` are REQUIRED fields
 * of the GT campaign object (gt_io_to_campaign.py:437-438, "Every field listed
 * below MUST be present"), gt_semantic_validator reads them to check every ad
 * group's flight against the campaign's (gt_semantic_validator.py:288-289,
 * :411-421), and applyGtConfigEdits already persists them.
 *
 * They used to be a hardcoded array inline in the campaign-configuration
 * card's JSX. That is what broke the bulk editor: the agent's campaign payload
 * is built from THIS projection, so a card field the projection did not know
 * about was invisible to the agent — which then told users, accurately about
 * its own payload and wrongly about the plan, that "GroundTruth has no
 * separate campaign-level start/end date field".
 *
 * Positions continue the descriptor's own sequence (which ends at 9) so the
 * card renders in exactly the order it did when these were appended by hand.
 */
const GT_CAMPAIGN_FLIGHT_FIELDS = [
  {
    key: 'start_date',
    configKey: 'start_date',
    label: 'Flight start',
    control: 'date',
    options: [],
    defaultValue: null,
    editable: true,
    writeBack: 'start_date',
    position: 10,
  },
  {
    key: 'end_date',
    configKey: 'end_date',
    label: 'Flight end',
    control: 'date',
    options: [],
    defaultValue: null,
    editable: true,
    writeBack: 'end_date',
    position: 11,
  },
];

/**
 * Campaign-scope fields the descriptor marks for the campaign-configuration
 * card (`summary` entries, generated from the Fields sheet's "Add to Summary"
 * decisions), in card order, plus the campaign flight. Each carries the
 * control the card renders: 'text' (free text), 'single-select' (options from
 * the sheet's static list) or 'date'. writeBack is the campaign.<key> the edit
 * persists to.
 *
 * A descriptor that DOES declare a campaign-scope flight field wins — the
 * code-level pair is a fallback for the generated sheet's omission, not an
 * override.
 */
export function gtCampaignSummaryFields(desc = activeDescriptor) {
  const fromDescriptor = Object.values(desc?.fields || {})
    .filter((f) => f.scope === 'campaign' && f.summary)
    .map((f) => ({
      key: f.rowKey,
      configKey: f.configKey,
      label: f.summary.label || f.label,
      control: f.control,
      options: Array.isArray(f.staticValues) ? f.staticValues : [],
      defaultValue: f.defaultValue || null,
      editable: f.editable !== false,
      writeBack: f.writeBack || f.configKey,
      position: f.summary.position ?? 0,
    }));
  const declared = new Set(fromDescriptor.map((f) => f.configKey));
  return [...fromDescriptor, ...GT_CAMPAIGN_FLIGHT_FIELDS.filter((f) => !declared.has(f.configKey))]
    .sort((a, b) => a.position - b.position)
    .map(({ position, ...f }) => f);
}

// ── Product scoping ─────────────────────────────────────────────────────────

const PRODUCT_ALIASES = {
  audience: 'audience',
  'on premise': 'onpremise',
  onpremise: 'onpremise',
  neighborhood: 'neighborhood',
  geotargeting: 'geotargeting',
};

export function normalizeGtProduct(product) {
  const s = String(product || '').trim().toLowerCase();
  return PRODUCT_ALIASES[s] || s;
}

/**
 * Whether a field applies to a row's product. `appliesToProducts` uses the
 * API-normalised product tokens (audience / onpremise / neighborhood /
 * geotargeting); null means every product.
 */
export function gtFieldAppliesTo(entry, product) {
  if (!entry?.appliesToProducts || entry.appliesToProducts.length === 0) return true;
  return entry.appliesToProducts.includes(normalizeGtProduct(product));
}

/**
 * The taxonomy `types` filter for a field, evaluated against the row.
 * Drive-To is the one dynamic case: its descriptor lists both candidate
 * types and the row's drive_to_type picks the one that applies.
 */
export function gtLookupTypesFor(fieldKey, row, desc = activeDescriptor) {
  const entry = getGtField(fieldKey);
  if (!entry?.lookup || entry.lookup.kind !== 'gt-taxonomy') return null;
  const types = entry.lookup.types || [];
  if (fieldKey === 'tracking_visits_to' && types.length > 1) {
    const driveToType = String(row?.drive_to_type || '').trim().toLowerCase();
    return driveToType === 'brand' ? ['brand'] : ['location_group'];
  }
  return types;
}

/**
 * UI labels for the GT taxonomy types the CLW pickers surface, in the order
 * the type selector presents them. The taxonomy itself also carries `brand`,
 * `behavior` and `category` records (the merged audiences cell spans all of
 * them), so those stay listed after the four primary picks. Labels are
 * display-only: the keys are what the lookup filters on and what launches.
 */
export const GT_TAXONOMY_TYPE_LABELS = {
  third_party: 'Predictive AI Audience',
  custom_audience: 'Custom Audience',
  segment: 'Third-Party Segment',
  location_group: 'Location Group',
  brand: 'Brand',
  behavior: 'Behavior',
  category: 'Category',
};
